import React, { useState, useEffect, useRef, useCallback } from 'react'

const DURATIONS = [4000, 5000]
const PILLS = ['Strategy', 'Research', 'Brainstorming', 'Revenue']

function fireParticles() {
  const canvas = document.getElementById('ptcl') as HTMLCanvasElement | null
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  canvas.width = window.innerWidth
  canvas.height = window.innerHeight
  const cx = canvas.width / 2
  const cy = canvas.height / 2
  const cols = ['#C9A84C', '#E8C96A', '#F0D98A', '#1B2A4A', '#ffffff', '#D4AF37']
  const ps = Array.from({ length: 110 }, () => {
    const a = Math.random() * Math.PI * 2
    const spd = 3 + Math.random() * 8
    return {
      x: cx,
      y: cy,
      vx: Math.cos(a) * spd,
      vy: Math.sin(a) * spd - Math.random() * 3,
      sz: 3 + Math.random() * 5,
      col: cols[Math.floor(Math.random() * cols.length)],
      alpha: 1,
      rot: Math.random() * Math.PI * 2,
      rs: (Math.random() - 0.5) * 0.2,
      circ: Math.random() > 0.5,
      g: 0.14 + Math.random() * 0.1,
    }
  })
  let f = 0
  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    f++
    ps.forEach((p) => {
      p.x += p.vx
      p.y += p.vy
      p.vy += p.g
      p.vx *= 0.98
      p.alpha -= 0.018
      p.rot += p.rs
      if (p.alpha <= 0) return
      ctx.save()
      ctx.globalAlpha = Math.max(0, p.alpha)
      ctx.fillStyle = p.col
      ctx.translate(p.x, p.y)
      ctx.rotate(p.rot)
      if (p.circ) {
        ctx.beginPath()
        ctx.arc(0, 0, p.sz / 2, 0, Math.PI * 2)
        ctx.fill()
      } else {
        ctx.fillRect(-p.sz / 2, -p.sz / 2, p.sz, p.sz * 0.6)
      }
      ctx.restore()
    })
    if (f < 85) requestAnimationFrame(draw)
    else ctx.clearRect(0, 0, canvas.width, canvas.height)
  }
  draw()
}

interface FTUModalProps {
  onClose: () => void
  onOpenTC: () => void
}

function TimerRing({ i }: { i: number }) {
  return (
    <div style={{ position: 'absolute', top: 16, right: 16, width: 30, height: 30 }}>
      <svg width="30" height="30" viewBox="0 0 30 30" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="15" cy="15" r="13" fill="none" stroke="var(--bd)" strokeWidth="2" />
        <circle
          id={'ftu-ring-' + i}
          cx="15"
          cy="15"
          r="13"
          fill="none"
          stroke="var(--navy)"
          strokeWidth="2"
          strokeDasharray="82"
          strokeDashoffset={82}
          strokeLinecap="round"
        />
      </svg>
    </div>
  )
}

export default function FTUModal({ onClose, onOpenTC }: FTUModalProps) {
  const [cur, setCur] = useState(0)
  const rafRef = useRef<(number | null)[]>([null, null])

  const stopTimers = useCallback(() => {
    rafRef.current.forEach((t) => {
      if (t != null) cancelAnimationFrame(t)
    })
    rafRef.current = [null, null]
  }, [])

  const goTo = useCallback(
    (idx: number) => {
      stopTimers()
      setCur(idx)
    },
    [stopTimers]
  )

  const startTimer = useCallback(
    (i: number) => {
      if (i >= 2) return
      const ring = document.getElementById('ftu-ring-' + i)
      if (!ring) return
      const dur = DURATIONS[i]
      const circ = 82
      ;(ring as SVGElement & { style: { strokeDashoffset: string } }).style.strokeDashoffset = String(circ)
      let start: number | null = null
      function tick(ts: number) {
        if (!start) start = ts
        const p = Math.min((ts - start) / dur, 1)
        ;(ring as SVGElement & { style: { strokeDashoffset: string } }).style.strokeDashoffset = String(circ * (1 - p))
        if (p < 1) rafRef.current[i] = requestAnimationFrame(tick)
        else goTo(i + 1)
      }
      rafRef.current[i] = requestAnimationFrame(tick)
    },
    [goTo]
  )

  useEffect(() => {
    const t = setTimeout(() => startTimer(0), 600)
    return () => {
      clearTimeout(t)
      stopTimers()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cur < 2) {
      const t = setTimeout(() => startTimer(cur), 80)
      return () => clearTimeout(t)
    }
  }, [cur]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => {
    if (cur < 2) goTo(cur + 1)
    else {
      fireParticles()
      const box = document.getElementById('ftu-box')
      if (box) {
        box.style.transition = 'all .75s cubic-bezier(.16,1,.3,1)'
        box.style.boxShadow =
          '0 0 0 4px rgba(201,168,76,.5),0 20px 60px rgba(201,168,76,.25)'
        setTimeout(() => {
          box.style.opacity = '0'
          box.style.transform = 'scale(1.03)'
          setTimeout(onClose, 300)
        }, 400)
      } else setTimeout(onClose, 750)
    }
  }

  const isFinal = cur === 2

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(27, 42, 74, 0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'bIn .3s ease both',
      }}
    >
      <canvas
        id="ptcl"
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 1001,
        }}
      />
      <div
        id="ftu-box"
        style={{
          background: 'var(--white)',
          borderRadius: 24,
          width: 480,
          maxWidth: '92vw',
          boxShadow: '0 24px 80px rgba(0,0,0,.35)',
          overflow: 'hidden',
          animation: 'mIn .5s cubic-bezier(.16,1,.3,1) both',
        }}
      >
        <div style={{ height: 3, background: 'rgba(0,0,0,.06)' }}>
          <div
            style={{
              height: '100%',
              background: 'var(--gold)',
              borderRadius: '0 2px 2px 0',
              width: `${((cur + 1) / 3) * 100}%`,
              transition: 'width .5s ease',
            }}
          />
        </div>
        <div
          key={cur}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: '48px 44px 32px',
            position: 'relative',
            animation: 'scIn .4s cubic-bezier(.16,1,.3,1) both',
          }}
        >
          {cur < 2 && <TimerRing i={cur} />}
          {cur === 0 && (
            <>
              <div
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: 24,
                  background: 'var(--navy)',
                  boxShadow: '0 8px 28px rgba(27,42,74,.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 24,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: -10,
                    borderRadius: 32,
                    border: '1.5px solid rgba(27,42,74,.14)',
                    animation: 'ring 2.5s ease-in-out infinite',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    inset: -22,
                    borderRadius: 40,
                    border: '1px solid rgba(27,42,74,.07)',
                    animation: 'ring 2.5s ease-in-out infinite .6s',
                  }}
                />
                <span
                  style={{
                    fontFamily: "'Playfair Display',serif",
                    fontSize: 40,
                    fontWeight: 600,
                    color: 'var(--gold)',
                  }}
                >
                  A
                </span>
              </div>
              <h2
                style={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: 24,
                  fontWeight: 500,
                  color: 'var(--navy)',
                  lineHeight: 1.32,
                  marginBottom: 11,
                }}
              >
                Meet <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>Athena</span> —
                <br />
                built for your business.
              </h2>
              <p
                style={{
                  fontSize: 13.5,
                  color: 'var(--muted)',
                  lineHeight: 1.72,
                  maxWidth: 340,
                }}
              >
                Athena thinks with you, available whenever you need it.
              </p>
            </>
          )}
          {cur === 1 && (
            <>
              <div
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: 24,
                  background: 'var(--blue-lt)',
                  border: '2px solid var(--blue-bd)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 24,
                }}
              >
                <span style={{ fontSize: 40, lineHeight: 1 }}>⚡</span>
              </div>
              <h2
                style={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: 24,
                  fontWeight: 500,
                  color: 'var(--navy)',
                  lineHeight: 1.32,
                  marginBottom: 11,
                }}
              >
                Ask <em style={{ color: 'var(--blue)' }}>anything</em>
                <br />
                about your business.
              </h2>
              <p
                style={{
                  fontSize: 13.5,
                  color: 'var(--muted)',
                  lineHeight: 1.72,
                  maxWidth: 340,
                }}
              >
                Ask about strategy, research, brainstorming, or anything in between.
              </p>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  justifyContent: 'center',
                  marginTop: 20,
                }}
              >
                {PILLS.map((p, i) => (
                  <div
                    key={p}
                    style={{
                      background: 'var(--bg)',
                      border: '1px solid var(--bd)',
                      borderRadius: 100,
                      padding: '6px 15px',
                      fontSize: 12.5,
                      color: 'var(--text)',
                      opacity: 0,
                      animation: `pillIn .4s cubic-bezier(.16,1,.3,1) ${i * 0.1 + 0.1}s both`,
                    }}
                  >
                    {p}
                  </div>
                ))}
              </div>
            </>
          )}
          {cur === 2 && (
            <>
              <div
                style={{
                  width: 88,
                  height: 88,
                  borderRadius: 24,
                  background: 'var(--gold-lt)',
                  border: '2px solid var(--gold-bd)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 24,
                }}
              >
                <span style={{ fontSize: 40, lineHeight: 1 }}>🤝</span>
              </div>
              <h2
                style={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: 24,
                  fontWeight: 500,
                  color: 'var(--navy)',
                  lineHeight: 1.32,
                  marginBottom: 11,
                }}
              >
                Athena <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>thinks</span>.
                <br />
                Your coach <em style={{ color: 'var(--blue)' }}>decides</em>.
              </h2>
              <p
                style={{
                  fontSize: 13.5,
                  color: 'var(--muted)',
                  lineHeight: 1.72,
                  maxWidth: 340,
                }}
              >
                Use Athena between sessions. For the big calls — your coach is still the one.
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  marginTop: 22,
                  width: '100%',
                  maxWidth: 360,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    borderRadius: 16,
                    padding: '18px 16px',
                    background: 'var(--navy)',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 22, marginBottom: 9 }}>🧠</div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '1.2px',
                      textTransform: 'uppercase',
                      color: 'var(--gold)',
                      marginBottom: 5,
                    }}
                  >
                    Athena
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      color: 'var(--white-soft)',
                    }}
                  >
                    Strategy, research and brainstorming.
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    borderRadius: 16,
                    padding: '18px 16px',
                    background: 'var(--bg)',
                    border: '1px solid var(--bd)',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 22, marginBottom: 9 }}>👤</div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '1.2px',
                      textTransform: 'uppercase',
                      color: 'var(--muted)',
                      marginBottom: 5,
                    }}
                  >
                    Your Coach
                  </div>
                  <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--muted)' }}>
                    Decisions, guidance and emotional intelligence.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div
          style={{
            padding: '6px 44px 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 11,
          }}
        >
          <div style={{ display: 'flex', gap: 6 }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                onClick={() => goTo(i)}
                style={{
                  width: cur === i ? 18 : 6,
                  height: 6,
                  borderRadius: cur === i ? 3 : '50%',
                  background: cur === i ? 'var(--navy)' : 'var(--bg2)',
                  cursor: 'pointer',
                  transition: 'all .3s',
                }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={handleNext}
            style={{
              width: '100%',
              background: 'var(--navy)',
              color: 'var(--white)',
              border: 'none',
              borderRadius: 14,
              padding: 14,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all .2s',
              animation: isFinal ? 'btnpulse 2s ease-in-out infinite' : 'none',
            }}
          >
            {isFinal && (
              <span
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '60%',
                  height: '100%',
                  background:
                    'linear-gradient(90deg,transparent,rgba(201,168,76,.22),transparent)',
                  animation: 'shine 2s ease-in-out infinite .3s',
                }}
              />
            )}
            {isFinal ? 'Enter Athena ✦' : 'Next →'}
          </button>
          <div
            style={{
              fontSize: 11,
              color: 'var(--muted2)',
              lineHeight: 1.6,
              textAlign: 'center',
            }}
          >
            By continuing you agree to the{' '}
            <span
              onClick={onOpenTC}
              style={{
                color: 'var(--blue)',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Terms of Use
            </span>{' '}
            for Athena.
          </div>
        </div>
      </div>
    </div>
  )
}
